/**
 * Loads the Google Maps JavaScript API once, with the browser key from VITE_GOOGLE_MAPS_API_KEY.
 * Google calls `window.gm_authFailure` when the key is rejected (invalid key, referrer not
 * allowed, billing disabled…). We expose that so the game can fall back to another source.
 */

declare global {
  interface Window {
    gm_authFailure?: () => void;
    __geogessrGoogleMapsReady?: () => void;
  }
}

const LOAD_TIMEOUT_MS = 20000;

let loadPromise: Promise<void> | null = null;
let authFailed = false;
const authFailureListeners = new Set<() => void>();

export function hasGoogleAuthFailed(): boolean {
  return authFailed;
}

export function onGoogleAuthFailure(listener: () => void): () => void {
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
}

export class GoogleMapsLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GoogleMapsLoadError';
  }
}

export function loadGoogleMaps(apiKey: string): Promise<void> {
  if (authFailed) return Promise.reject(new GoogleMapsLoadError('La clé Google Maps a été refusée.'));
  if (typeof window.google?.maps?.importLibrary === 'function') return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    const timer = window.setTimeout(() => fail('Le chargement de Google Maps a expiré.'), LOAD_TIMEOUT_MS);

    function fail(message: string) {
      window.clearTimeout(timer);
      loadPromise = null;
      script.remove();
      reject(new GoogleMapsLoadError(message));
    }

    window.__geogessrGoogleMapsReady = () => {
      window.clearTimeout(timer);
      resolve();
    };
    window.gm_authFailure = () => {
      authFailed = true;
      authFailureListeners.forEach((listener) => listener());
    };

    const params = new URLSearchParams({
      key: apiKey,
      v: 'weekly',
      language: 'fr',
      loading: 'async',
      callback: '__geogessrGoogleMapsReady',
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => fail('Impossible de joindre Google Maps (réseau ou bloqueur de contenu).');
    document.head.appendChild(script);
  });
  return loadPromise;
}

export async function importStreetView(apiKey: string): Promise<google.maps.StreetViewLibrary> {
  await loadGoogleMaps(apiKey);
  return (await google.maps.importLibrary('streetView')) as google.maps.StreetViewLibrary;
}

export async function importMaps(apiKey: string): Promise<{ maps: google.maps.MapsLibrary; core: google.maps.CoreLibrary }> {
  await loadGoogleMaps(apiKey);
  const [maps, core] = await Promise.all([
    google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>,
    google.maps.importLibrary('core') as Promise<google.maps.CoreLibrary>,
  ]);
  return { maps, core };
}
